/**
 * Compare snapping: when a carried panel is released near another, it docks beside it.
 * Pure geometry so it can be unit tested.
 */
import * as THREE from 'three';
import { PANEL_W } from './Dashboard.ts';

export interface PanelPose {
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
  scale: number;
}

export const SNAP_DISTANCE = 0.6;

/** Picks the nearest other panel within the snap distance, else undefined. */
export function snapCandidate<T extends { group: THREE.Object3D }>(
  released: T,
  others: T[],
): T | undefined {
  const p = released.group.getWorldPosition(new THREE.Vector3());
  let best: T | undefined;
  let bestD = Infinity;
  for (const o of others) {
    if (o === released) continue;
    const q = o.group.getWorldPosition(new THREE.Vector3());
    // Measure to the panel's near edge, so wide panels still snap at a sensible gap.
    const d = Math.max(0, p.distanceTo(q) - (PANEL_W * o.group.scale.x) / 2);
    if (d < bestD) {
      bestD = d;
      best = o;
    }
  }
  return bestD <= SNAP_DISTANCE ? best : undefined;
}

/** The pose that puts `width`-wide panel at `scale` to the right of `anchor`, same facing. */
export function dockBeside(anchor: PanelPose, scale: number, width = PANEL_W): PanelPose {
  const right = new THREE.Vector3(1, 0, 0).applyQuaternion(anchor.quaternion);
  const gap = 0.06;
  const offset = (width * anchor.scale) / 2 + gap + (width * scale) / 2;
  return {
    position: anchor.position.clone().addScaledVector(right, offset),
    quaternion: anchor.quaternion.clone(),
    scale,
  };
}
